/**
 * @file shared/desktop/useDesktopSettings.ts
 * @brief The desktop app's settings (⌘,) for a page, live.
 *
 * @details
 * DES 02-10-2026. Null in a browser tab, and until the app has answered.
 * `useDesktopDefaultMic` gives Boardroom setup and the Interpreter the mic
 * chosen in Settings as their starting choice; the page's own picker still
 * changes it for that meeting.
 *
 * @author DES
 * @date 02-10-2026
 */

import { useEffect, useRef, useState } from "react";
import { desktopShell, type DesktopSettings } from "./desktopShell";

export function useDesktopSettings(): DesktopSettings | null {
  const [settings, setSettings] = useState<DesktopSettings | null>(null);
  useEffect(() => {
    const shell = desktopShell();
    if (!shell?.getSettings) return;
    let alive = true;
    void shell.getSettings().then((s) => {
      if (alive && s) setSettings(s);
    });
    const off = shell.onSettings?.((s) => setSettings(s));
    return () => {
      alive = false;
      off?.();
    };
  }, []);
  return settings;
}

/**
 * The Settings mic, matched against this page's device list by id, then by
 * name (ids can change when a mic is re-plugged). Applied once, and only while
 * the page is still on "System default", so it never overrides a choice made
 * on the page.
 */
export function useDesktopDefaultMic(
  devices: { deviceId: string; label: string }[],
  deviceId: string,
  setDeviceId: (id: string) => void,
): void {
  const settings = useDesktopSettings();
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current || !settings || devices.length === 0) return;
    applied.current = true;
    if (deviceId || !settings.micDeviceId) return;
    const match =
      devices.find((d) => d.deviceId === settings.micDeviceId) ??
      (settings.micLabel ? devices.find((d) => d.label === settings.micLabel) : undefined);
    if (match) setDeviceId(match.deviceId);
  }, [settings, devices, deviceId, setDeviceId]);
}
